<template>
  <v-container>
    <v-layout>
      <v-flex v-if="data.hasOwnProperty('user') || data.hasOwnProperty('tasks')">
        <h2>Does this look right?</h2>
        <v-expansion-panels popout multiple class="mt-2">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <v-layout align-center row spacer>
                <v-flex xs4 sm2 md1>
                  <v-avatar size="36px">
                    <v-icon>{{ accountsCount === 1 ? 'account_circle' : 'supervised_user_circle' }}</v-icon>
                  </v-avatar>
                </v-flex>
                <v-flex sm5 md3 hidden-xs-only>
                  <strong>{{ accountsText | capitalize }} Information</strong>
                </v-flex>
                <v-flex class="grey--text">
                  <template v-if="isCreate">
                    {{ accountsCount }} new {{ accountsText }}
                  </template>
                  <template v-else>
                    Update {{ accountsCount }} {{ accountsText }}
                  </template>
                </v-flex>
              </v-layout>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card>
                <v-divider></v-divider>
                <v-card-text>
                  <dl class="ml-2">
                    <dt>Username{{ accountsCount === 1 ? '' : 's' }}</dt>
                    <dd>{{ usernames }}</dd>

                    <template v-if="data.user.location">
                      <dt>Location</dt>
                      <dd>{{ data.user.location }}</dd>
                    </template>

                    <template v-if="data.user.user_type && isCreate">
                      <dt>Account Type</dt>
                      <dd>{{ data.user.user_type }}</dd>
                    </template>

                    <template v-if="data.user.options && data.user.options.length !== 0 && isCreate">
                      <dt>Options</dt>
                      <dd>{{ data.user.options.join(', ') }}</dd>
                    </template>

                    <template v-if="data.user.first_name && isCreate">
                      <dt>First Name</dt>
                      <dd>{{ data.user.first_name }}</dd>
                    </template>

                    <template v-if="data.user.last_name && isCreate">
                      <dt>Last Name</dt>
                      <dd>{{ data.user.last_name }}</dd>
                    </template>

                    <template v-if="data.user.email && isCreate">
                      <dt>Email</dt>
                      <dd>{{ data.user.email }}</dd>
                    </template>

                    <template v-if="data.user.expires_at">
                      <dt>Expires on</dt>
                      <dd>{{ data.user.expires_at }}</dd>
                    </template>
                  </dl>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel v-if="retestTasks">
            <v-expansion-panel-header>
              <v-layout align-center row spacer>
                <v-flex xs4 sm2 md1>
                  <v-avatar slot="activator" size="36px">
                    <v-icon>assessment</v-icon>
                  </v-avatar>
                </v-flex>
                <v-flex sm5 md3 hidden-xs-only>
                  <strong>Assessment Tasks Reset</strong>
                </v-flex>
                <v-flex class="grey--text" ellipsis>
                  <template>
                    <task-config-summary :taskConfig="retestTasksSummary"/>
                  </template>
                </v-flex>
              </v-layout>
            </v-expansion-panel-header>
          </v-expansion-panel>
          <!--Assessment Config -->
          <v-expansion-panel v-if="assessmentConfig">
            <v-expansion-panel-header>
              <v-layout align-center row spacer>
                <v-flex xs4 sm2 md1>
                  <v-avatar slot="activator" size="36px">
                    <v-icon>assessment</v-icon>
                  </v-avatar>
                </v-flex>
                <v-flex sm5 md3 hidden-xs-only>
                  <strong>Assessment Config</strong>
                </v-flex>
                <v-flex class="grey--text" ellipsis>
                  <template>
                    <strong>{{ this.assessmentConfig.name }}</strong> ({{ tasksCountText }})
                  </template>
                </v-flex>
              </v-layout>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card>
                <v-divider></v-divider>
                <v-card-text>
                  <dl class="dl-card-alig">
                    <template v-if="this.assessmentConfig.description">
                      <dt>Description</dt>
                      <dd>{{ this.assessmentConfig.description }}</dd>
                    </template>

                    <dt class="mt-10px">Tasks</dt>
                    <dd>
                      <task-config-summary :taskConfig="assessmentConfig.config.tasks"/>
                    </dd>
                  </dl>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel v-if="displayScheduling">
            <v-expansion-panel-header>
              <v-layout align-center row spacer>
                <v-flex xs4 sm2 md1>
                  <v-avatar slot="activator" size="36px">
                    <v-icon>build</v-icon>
                  </v-avatar>
                </v-flex>
                <v-flex sm5 md3 hidden-xs-only>
                  <strong>Scheduling</strong>
                </v-flex>
              </v-layout>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card>
                <v-divider></v-divider>
                <v-card-text>
                  <dl class="dl-card-align">
                    <dt>Duration Modifier</dt>
                    <dd v-if="data.user.assessment_duration_modifier !== null">
                      {{ data.user.assessment_duration_modifier }}
                    </dd>
                    <dd v-else><em>Multiple Values</em></dd>

                    <dt>Time Slot Selection</dt>
                    <dd v-if="data.user.assessment_skip_scheduling !== null">
                      {{ data.user.assessment_skip_scheduling ? 'Optional' : 'Required' }}
                    </dd>
                    <dd v-else><em>Multiple Values</em></dd>

                    <dt>Start Date</dt>
                    <dd v-if="data.user.assessment_start_date !== null">
                      {{ data.user.assessment_start_date | dateTime }}
                    </dd>
                    <dd v-else-if="data.user.assessment_date === null && data.user.isAssessmentDateNullForAll">
                      Now
                    </dd>
                    <dd v-else><em>Multiple Values</em></dd>

                    <dt>End Date</dt>
                    <dd v-if="data.user.assessment_end_date !== null">
                      {{ data.user.assessment_end_date | dateTime }}
                    </dd>
                    <dd v-else><em>Multiple Values</em></dd>

                    <dt>Login Window</dt>
                    <dd v-if="data.user.assessment_date !== null">
                      {{ data.user.assessment_date | dateTime }}
                    </dd>
                    <dd v-else-if="data.user.assessment_date === null && data.user.isAssessmentDateNullForAll">
                      -
                    </dd>
                    <dd v-else><em>Multiple Values</em></dd>
                    <dt>Selected Service Version</dt>
                    <dd v-if="data.user.client_version_id !== null">
                    {{ userSelectedClientVersion.version  }}::{{ userSelectedClientVersion.image_tag }}
                    </dd>
                    <dd v-else-if="data.user.client_version_id === null">
                      -
                    </dd>
                    <dd v-else><em>Not selected</em></dd>
                  </dl>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        <v-expansion-panel v-if="displayScheduling">
            <v-expansion-panel-header>
              <v-layout align-center row spacer>
                <v-flex xs4 sm2 md1>
                  <v-avatar slot="activator" size="36px">
                    <v-icon>done</v-icon>
                  </v-avatar>
                </v-flex>
                <v-flex sm5 md3 hidden-xs-only>
                  <strong>Skip Tech Check</strong>
                </v-flex>
              </v-layout>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card>
                <v-divider></v-divider>
                <v-card-title>
                  Allow player to bypass failed Launcher tech checks
                  </v-card-title>
                <v-card-text>
                      {{ skipTechChecks }}
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>

        </v-expansion-panels>
      </v-flex>
    </v-layout>
    <v-layout class="mt-4">
      <v-flex>
        <v-toolbar color="white" flat>
          <v-spacer></v-spacer>
          <v-btn color="success darken-2"
                 @click="next"
                 class="pl-3"
                 data-test="finishAccountsSteps">
            <v-icon class="px-1" left>check</v-icon>
            <span v-if="isCsv">Confirm And Download CSV</span>
            <span v-else>Confirm</span>
          </v-btn>
        </v-toolbar>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import FormStep from '../../../mixins/FormStep'
import TaskConfigSummary from '../../Tasks/TaskConfigSummary'

export default {
  name: 'AccountReviewStep',
  components: {
    TaskConfigSummary
  },
  mixins: [FormStep],
  computed: {
    ...mapState('assessmentConfigs', {
      assessmentConfigs: 'assessmentConfigs'
    }),
    skipTechChecks () {
      var skip = this.data.user.assessment_skip_tech_checks ? 'Yes' : 'No'
      return skip
    },
    userSelectedClientVersion () {
      if (!this.data.user || !this.data.user.client_version_id) {
        return { image_tag: 'missing!', version: 'not found!' }
      }
      const serviceVersion = this.$store.state.clientServiceVersions.serviceVersions[this.data.user.client_version_id]
      if (!serviceVersion) {
        return { image_tag: 'missing!', version: 'not found!' }
      }
      return serviceVersion
    },
    /**
     * Is CSV file uploaded
     */
    isCsv () {
      return this.data.csv && this.data.csv.users.length > 0
    },
    /**
     * If true, the assessment config section must be displayed
     *
     * @returns {boolean}
     */
    displayScheduling () {
      return this.data.user.assessment_duration_modifier !== null ||
        this.data.user.assessment_skip_scheduling !== null ||
        this.data.user.assessment_start_date !== null ||
        this.data.user.assessment_end_date !== null ||
        this.data.user.assessment_date !== null ||
        this.data.user.client_version_id !== null
    },
    /**
     * Returns the number of accounts that were created or updated
     *
     * @returns {number}
     */
    accountsCount () {
      return ('users' in this.data && this.data.users.length !== 0) ? this.data.users.length : 1
    },
    /**
     * Returns either "account" or "accounts"
     *
     * @returns {string}
     */
    accountsText () {
      return 'account' + (this.accountsCount === 1 ? '' : 's')
    },
    /**
     * Returns a list of usernames (either contained in the users array, or in user.username if a single account was created)
     *
     * @returns {array}
     */
    usernames () {
      return this.accountsCount > 1
        ? this.data.users.map(user => user.username).join(', ')
        : this.data.user.username
    },
    /**
     * Returns the taskGroup object if an existing taskGroup was selected, otherwise null
     *
     * @returns {*}
     */
    assessmentConfig () {
      if (this.data.assessmentConfigId !== null) {
        return this.assessmentConfigs[this.data.assessmentConfigId]
      }

      if (this.data.customConfig !== null) {
        return {
          name: 'Custom Config',
          description: '',
          config: this.data.customConfig
        }
      }

      return null
    },
    /**
     * the tasks to be reset
     */
    retestTasks () {
      if (this.data.retestTasks && this.data.retestTasks.length) {
        return this.data.retestTasks
      }
      return null
    },
    retestTasksSummary () {
      if (this.retestTasks && this.retestTasks.length) {
        return { names: this.retestTasks, parameters: {} }
      }
      return { names: [], parameters: {} }
    },
    /**
     * Returns the number of tasks in this assessment
     *
     * @returns {number}
     */
    tasksCount () {
      if (this.assessmentConfig) {
        return this.assessmentConfig.config.tasks.names.length
      }
      return 0
    },
    /**
     * Returns either "task" or "tasks"
     *
     * @returns {string}
     */
    tasksCountText () {
      return `${this.tasksCount} task${this.tasksCount === 1 ? '' : 's'}`
    }
  }
}
</script>
<style lang="sass">
  .v-expansion-panel::before
    box-shadow: none
</style>
